import { type ReactNode, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountQuery, hostBaseUrl } from '@/api';
import { accountLogoUrl, shellVisibilityFromAccount } from '@/domain';
import { cn } from '@/lib/cn';
import { usePlayerSocket } from '@/player';
import { FreeSpinsBonusHost } from '@/ui/casino/FreeSpinsBonusHost';
import { PromoPopupModal, TopInfoTicker } from '@/ui/cms';
import { DepositBonusToastHost } from '@/ui/profile/DepositBonusToastHost';
import { PrematchStatisticsHost } from '@/ui/sports/PrematchStatisticsHost';
import { RewardsNotificationBanner } from '@/ui/rewards';
import { BackToTop } from './BackToTop';
import { Footer } from './Footer';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileHeader } from './MobileHeader';
import { SideNav } from './SideNav';
import { SportsSearchProvider } from './SportsSearchContext';
import { SportsSearchOverlay } from './SportsSearchOverlay';
import { TopNav } from './TopNav';

export interface AppShellProps {
  children: ReactNode;
  /** Betslip rail, shown beside the content on wide viewports. */
  aside?: ReactNode;
  /** When true, the desktop sports SideNav is not rendered. */
  hideSideNav?: boolean;
  /** When true, the site footer is omitted (e.g. mobile betslip). */
  hideFooter?: boolean;
  /** When true, main content has no padding (account shell owns its gutters). */
  flushMain?: boolean;
}

export function AppShell({
  children,
  aside,
  hideSideNav = false,
  hideFooter = false,
  flushMain = false,
}: AppShellProps) {
  const { data: account } = useQuery(accountQuery());
  usePlayerSocket();
  const [searchOpen, setSearchOpen] = useState(false);
  const shellVisibility = shellVisibilityFromAccount(account);
  const logoUrl =
    shellVisibility.showLogo && account
      ? accountLogoUrl(hostBaseUrl('users'), account)
      : null;

  useEffect(() => {
    const title = account?.Title?.trim() || account?.Name?.trim();
    if (title) document.title = title;
  }, [account?.Title, account?.Name]);

  return (
    <SportsSearchProvider openSearch={() => setSearchOpen(true)}>
      <div
        className={cn(
          'flex min-h-dvh flex-col bg-bg-canvas',
          // Mobile betslip: lock shell height so the page cannot overscroll under the CTA.
          hideFooter && 'max-lg:h-dvh max-lg:overflow-hidden',
        )}
      >
        <TopInfoTicker />
        <TopNav logoUrl={logoUrl} />
        <MobileHeader logoUrl={logoUrl} />
        <div className="px-4 pt-2 xl:hidden">
          <RewardsNotificationBanner mobileOnly />
        </div>

        <div
          className={cn(
            'flex w-full flex-1 items-start',
            hideFooter && 'max-lg:min-h-0 max-lg:overflow-hidden',
          )}
        >
          {!hideSideNav && (
            <SideNav
              className="sticky top-14 hidden max-h-[calc(100dvh-3.5rem)] overflow-y-auto [scrollbar-gutter:stable] lg:flex"
              onOpenSearch={() => setSearchOpen(true)}
            />
          )}

          <main
            className={cn(
              'min-w-0 flex-1',
              flushMain ? 'p-0' : 'px-4 py-4 lg:px-6',
              hideFooter && 'max-lg:min-h-0 max-lg:overflow-hidden max-lg:p-0',
            )}
          >
            {children}
          </main>

          {aside && (
            <aside className="sticky top-14 hidden h-[calc(100dvh-3.5rem)] max-h-[calc(100dvh-3.5rem)] w-80 shrink-0 overflow-hidden pt-3 pr-4 pb-3 xl:flex xl:flex-col">
              {aside}
            </aside>
          )}
        </div>

        {shellVisibility.showFooter && !hideFooter && <Footer logoUrl={logoUrl} />}
        <MobileBottomNav skipSpacer={hideFooter} />
        <BackToTop />
        <SportsSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
        <PromoPopupModal />
        <DepositBonusToastHost />
        <FreeSpinsBonusHost />
        <PrematchStatisticsHost />
      </div>
    </SportsSearchProvider>
  );
}
